<!-- ============================================================
  SheetSubgroupBlock.vue — one coach-subgroup block inside a session
  column of the grouping sheet (or, with subgroup=null, the special
  "ยังไม่จัดกลุ่ม" block). Mirrors one colored block of the old Google
  Sheet: colored header (coach names / special group name + count),
  then one compact row per student.

  Manager mode: clicking the header emits `edit` (edit-group dialog),
  clicking a student row emits `assign` (move-to-group menu anchored
  to the row). readonly hides both affordances (coach view).
============================================================ -->
<template>
  <div class="gsb" :class="{ mine: isMine && highlightMine, dimmed: dimmed, absent: absent }">
    <div class="gsb-head" :style="headStyle" :class="{ clickable: !readonly && subgroup }"
      @click="!readonly && subgroup && $emit('edit')">
      <span v-if="isMine && highlightMine" class="mdi mdi-star gsb-star"></span>
      <span class="gsb-title">{{ title }}</span>
      <span v-if="coachSuffix" class="gsb-coaches">{{ coachSuffix }}</span>
      <span class="gsb-count">{{ students.length }}</span>
      <span v-if="absent" class="gsb-absent"><span class="mdi mdi-account-off"></span> {{ $t('grouping.absentTag') }}</span>
      <span v-if="!readonly && subgroup" class="mdi mdi-pencil gsb-editico"></span>
    </div>
    <div v-if="subgroup && subgroup.remark" class="gsb-remark">
      <span class="mdi mdi-note-text-outline"></span> {{ subgroup.remark }}
    </div>
    <div v-if="students.length === 0" class="gsb-empty">{{ subgroup ? $t('grouping.emptyGroup') : $t('grouping.allAssigned') }}</div>
    <!-- v3 split: ผู้เข้าร่วมอีเวนต์ไม่มี reservationid (มีแต่ registrationid) — ถ้าคีย์ด้วย
         reservationid อย่างเดียว ทุกแถวของอีเวนต์จะได้ key undefined เหมือนกันหมด แล้ว Vue
         จะใช้ DOM แถวผิดตัวหลังเช็คชื่อ/รีเฟรช (จุดจาก codex review fe #177) -->
    <div v-for="(st, i) in students" :key="st.memberkey || st.reservationid" class="gsb-row"
      :class="{ checked: hasMarker(st, '(1)'), trial: hasMarker(st, '(blue)'), session: hasMarker(st, '(pink)'), clickable: !readonly }"
      @click="!readonly && $emit('assign', { student: st, event: $event })">
      <span class="gsb-num">{{ i + 1 }}</span>
      <span class="gsb-name">{{ displayName(st) }}</span>
      <span v-if="hasMarker(st, '(blind)')" class="mdi mdi-eye-off gsb-ico" style="color:#64748b"></span>
      <!-- โหมดเช็คอิน (หน้าตารางสอนโค้ช): ปุ่มติ๊กเช็คชื่อได้เหมือน Dashboard;
           นอกโหมดนี้แสดงเป็นไอคอนสถานะเฉย ๆ -->
      <button v-if="checkinMode" class="gsb-checkbtn" :class="{ on: hasMarker(st, '(1)') }"
        @click.stop="$emit('checkin', st)"
        v-hint="hasMarker(st, '(1)') ? $t('grouping.undoCheckin') : $t('grouping.checkin')">
        <span class="mdi" :class="hasMarker(st, '(1)') ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline'"></span>
      </button>
      <span v-else-if="hasMarker(st, '(1)')" class="mdi mdi-check-circle gsb-ico" style="color:#10b981"></span>
      <span v-if="!readonly" class="mdi mdi-chevron-down gsb-ico gsb-caret"></span>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // null = the "unassigned" block
    subgroup: { type: Object, default: null },
    students: { type: Array, default: () => [] },
    // coachid -> { displayname, color, ... }
    coachMap: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    absent: { type: Boolean, default: false },
    isMine: { type: Boolean, default: false },
    highlightMine: { type: Boolean, default: false },
    dimmed: { type: Boolean, default: false },
    // แสดงปุ่มเช็คอินคลิกได้ต่อเด็ก (หน้าตารางสอนโค้ช)
    checkinMode: { type: Boolean, default: false },
  },
  emits: ['edit', 'assign', 'checkin'],
  computed: {
    coachNames() {
      if (!this.subgroup) return ''
      return (this.subgroup.coachids || [])
        .map((id) => (this.coachMap[id] && this.coachMap[id].displayname) || `#${id}`)
        .join('/')
    },
    title() {
      if (!this.subgroup) return this.$t('grouping.unassigned')
      return this.subgroup.subgroupname || this.coachNames || '—'
    },
    // เมื่อมีทั้งชื่อกลุ่มพิเศษและโค้ช แสดงชื่อโค้ชต่อท้ายตัวเล็ก
    coachSuffix() {
      if (!this.subgroup || !this.subgroup.subgroupname) return ''
      return this.coachNames
    },
    headStyle() {
      if (!this.subgroup) {
        return { background: 'var(--c-warning-bg)', color: 'var(--c-warning-tx)' }
      }
      const first = (this.subgroup.coachids || []).map((id) => this.coachMap[id]).find((c) => c && c.color)
      const color = (first && first.color) || '#8b5cf6'
      return { background: `${color}22`, borderLeft: `4px solid ${color}` }
    },
  },
  methods: {
    hasMarker(st, marker) {
      return typeof st.name === 'string' && st.name.includes(marker)
    },
    displayName(st) {
      if (typeof st.name !== 'string') return ''
      return st.name
        .replace('(1)', '').replace('(red)', '').replace('(green)', '').replace('(blue)', '')
        .replace('(yellow)', '').replace('(pink)', '').replace('(pay)', '').replace('(blind)', '')
        .trim()
    },
  },
}
</script>

<style scoped>
.gsb { border: 1px solid var(--c-border); border-radius: 10px; overflow: hidden; background: var(--c-surface); }
.gsb.mine { border: 2px solid var(--c-primary); box-shadow: var(--shadow-sm); }
.gsb.dimmed { opacity: 0.55; }
.gsb.absent { border-color: var(--c-error); }
.gsb-head { display: flex; align-items: center; gap: 6px; padding: 6px 10px; font-size: 13px; font-weight: 700; flex-wrap: wrap; }
.gsb-head.clickable { cursor: pointer; }
.gsb-star { color: var(--c-primary); font-size: 15px; }
.gsb-title { overflow-wrap: anywhere; }
.gsb-coaches { font-weight: 400; font-size: 11.5px; opacity: 0.8; }
.gsb-count { font-weight: 400; font-size: 11.5px; background: rgba(0,0,0,0.08); border-radius: 999px; padding: 0 7px; }
.gsb-absent { margin-left: auto; color: #fff; background: var(--c-error); border-radius: 999px; padding: 1px 8px; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; gap: 3px; }
.gsb-editico { font-size: 14px; opacity: 0.5; margin-left: auto; }
.gsb-absent + .gsb-editico { margin-left: 0; }
.gsb-remark { font-size: 11.5px; color: var(--c-text-muted); padding: 4px 10px; border-top: 1px dashed var(--c-border); display: flex; gap: 5px; align-items: flex-start; }
.gsb-remark .mdi { font-size: 13px; }
.gsb-empty { font-size: 12px; color: var(--c-text-muted); padding: 8px 10px; text-align: center; }
.gsb-row { display: flex; align-items: center; gap: 7px; padding: 5px 10px; font-size: 12.5px; border-top: 1px solid var(--c-border); }
.gsb-row.clickable { cursor: pointer; }
.gsb-row.clickable:hover { background: var(--c-surface-3); }
.gsb-row.checked { background: var(--c-success-bg); }
.gsb-row.trial { box-shadow: inset 3px 0 0 var(--c-trial); }
.gsb-row.session { box-shadow: inset 3px 0 0 var(--c-session); }
.gsb-num { flex: 0 0 18px; text-align: right; color: var(--c-text-muted); font-size: 11.5px; }
.gsb-name { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.gsb-ico { font-size: 14px; flex: 0 0 auto; }
.gsb-caret { color: var(--c-text-muted); }
.gsb-checkbtn { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; border: none; background: transparent; cursor: pointer; padding: 2px; border-radius: 999px; color: var(--c-text-muted); }
.gsb-checkbtn .mdi { font-size: 18px; }
.gsb-checkbtn:hover { background: var(--c-surface-3); }
.gsb-checkbtn.on { color: #10b981; }
</style>
